import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { checkoutAPI } from '../lib/api';
import { toast } from 'sonner';
import { trackBeginCheckout } from '../lib/analytics';

const CheckoutPage = () => {
  const { cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  // Fire begin_checkout / InitiateCheckout once when the checkout page loads with items.
  useEffect(() => {
    if (cart?.items?.length) {
      trackBeginCheckout(cart.items, cart.total_amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async () => {
    try {
      setProcessing(true);
      const response = await checkoutAPI.initCheckout();
      const { payfast_url, payfast_fields } = response.data;

      // Create and submit form to PayFast
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payfast_url;

      Object.entries(payfast_fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      toast.error('Failed to initialize payment. Please try again.');
      setProcessing(false);
    }
  };

  if (!cart?.items || cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="checkout-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 
          className="text-4xl font-black text-primary mb-8"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div>
            <div className="bg-white border border-border p-6 mb-6">
              <h2 className="text-xl font-bold text-primary mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Order Summary</h2>
              <div className="space-y-4">
                {cart.items.map((item, index) => (
                  <div key={index} className="pb-4 border-b border-border last:border-0" data-testid={`order-item-${index}`}>
                    <div className="font-bold text-primary mb-1">{item.plan_name}</div>
                    <div className="text-sm text-text_muted">
                      {item.config.file_type} • {item.config.drawing_sets.join(', ')}
                    </div>
                    <div className="text-accent font-bold mt-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      R{item.total_price.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-border p-6">
              <h2 className="text-xl font-bold text-primary mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Customer Information</h2>
              <dl className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <dt className="text-text_muted">Name:</dt>
                  <dd className="font-medium text-primary">{user?.name}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-text_muted">Email:</dt>
                  <dd className="font-medium text-primary">{user?.email}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Payment */}
          <div>
            <div className="bg-white border border-border p-6">
              <h2 className="text-xl font-bold text-primary mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Payment</h2>
              
              <div className="mb-6 pb-6 border-b border-border">
                <div className="flex justify-between mb-2">
                  <span className="text-text_muted">Subtotal:</span>
                  <span className="font-medium">R{cart.total_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-4">
                  <span>Total:</span>
                  <span className="text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    R{cart.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 border border-border p-4 mb-6">
                <p className="text-sm text-text_muted mb-2">You will be redirected to PayFast to complete your payment securely.</p>
                <div className="flex items-center text-xs text-text_muted">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure payment powered by PayFast
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={processing}
                className="w-full bg-primary text-white py-4 rounded font-bold hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pay-now-btn"
              >
                {processing ? 'Processing...' : 'Pay Now'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;